import homeOg from "@/assets/hero-home-aesthetic.webp";
import bokforingOg from "@/assets/hero-bokforing-dark.webp";
import bokslutOg from "@/assets/hero-bokslut-dark.webp";
import radgivningOg from "@/assets/hero-radgivning-dark.webp";
import inhouseOg from "@/assets/hero-inhouse-dark.webp";
import lonerOg from "@/assets/hero-loner-dark.webp";
import omOssOg from "@/assets/oom-oss.webp";
import kontaktUmeaOg from "@/assets/hero-umea.webp";
import kontaktStockholmOg from "@/assets/hero-stockholm.webp";
import karriarOg from "@/assets/karrar.webp";
import nyheterOg from "@/assets/news-award.webp";
import referenserOg from "@/assets/refernce.webp";
import teamOg from "@/assets/team-lisa.webp";
import { articleBySlug, type Article } from "@/data/articles";

export const SITE_URL = "https://cloudredovisning.se";
export const SITE_NAME = "Cloud Redovisning & Administration";
export const SITE_PHONE = "+46 70 303 63 45";
export const SITE_PHONE_E164 = "+46703036345";
export const SITE_EMAIL = "info@cloudredovisning.se";

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

// Stable (un-hashed) image URLs for structured data. Files in public/images are copied as-is by
// every build, unlike src/assets imports whose file names change with each build.
export const STABLE_IMAGES = {
  logo: `${SITE_URL}/images/cloud-redovisning-logo.png`,
  umea: `${SITE_URL}/images/office-umea.webp`,
  stockholm: `${SITE_URL}/images/office-stockholm.webp`,
} as const;

export const SOCIAL_PROFILES = [
  "https://www.facebook.com/Cloud-Redovisning-Administration-114152356669953",
];

// Phone number used in the office markup. It must be identical on the page, in the markup and on
// the Google Business Profile. TODO: confirm the number the client chooses (brief section 9).
export const OFFICE_PHONE_E164 = SITE_PHONE_E164;
export const SITE_DESCRIPTION =
  "Auktoriserad redovisningsbyrå i Umeå och Stockholm med digital bokföring, löneadministration, bokslut, deklaration och affärsrådgivning för företag i hela Sverige.";

type Schema = Record<string, unknown>;

export interface SeoPageConfig {
  key: string;
  path: string;
  title: string;
  description: string;
  image?: string;
  focusKeyword: string;
  secondaryKeywords: string[];
}

export const publicPageSeo: Record<string, SeoPageConfig> = {
  home: {
    key: "home",
    path: "/",
    title: "Redovisningsbyrå i Umeå & Stockholm | Cloud Redovisning",
    description:
      "Auktoriserad redovisningsbyrå i Umeå och Stockholm för bokföring, lön, bokslut, deklaration och rådgivning. Digital hjälp i hela Sverige.",
    image: homeOg,
    focusKeyword: "redovisningsbyrå Umeå",
    secondaryKeywords: [
      "redovisningsbyrå Stockholm",
      "digital bokföring",
      "löneadministration Umeå",
      "ekonomisk rådgivning företag",
    ],
  },
  bokforing: {
    key: "bokforing",
    path: "/bokforing",
    title: "Löpande bokföring i Fortnox | Cloud Redovisning",
    description:
      "Digital bokföring för företag i Umeå, Stockholm och hela Sverige. Få löpande bokföring, rapporter och rådgivning från personlig redovisningskonsult.",
    image: bokforingOg,
    focusKeyword: "bokföring Umeå",
    secondaryKeywords: [
      "bokföring Stockholm",
      "digital bokföring",
      "redovisningskonsult Umeå",
      "redovisningsbyrå Stockholm",
    ],
  },
  bokforingUmea: {
    key: "bokforing-umea",
    path: "/bokforing-umea",
    title: "Bokföring i Umeå – digitalt i Fortnox | Cloud Redovisning",
    description:
      "Bokföring i Umeå för företag som vill ha digital hantering i Fortnox och en personlig kontakt. Kontor på Pendelgatan 3b. Boka kostnadsfritt möte.",
    image: kontaktUmeaOg,
    focusKeyword: "bokföring Umeå",
    secondaryKeywords: [
      "redovisningsbyrå Umeå",
      "digital bokföring Umeå",
      "redovisningskonsult Umeå",
      "löpande bokföring Umeå",
    ],
  },
  bokforingStockholm: {
    key: "bokforing-stockholm",
    path: "/bokforing-stockholm",
    title: "Bokföring i Stockholm – digitalt i Fortnox | Cloud Redovisning",
    description:
      "Bokföring i Stockholm i Fortnox med fast kontaktperson, daglig bankavstämning och månadsrapport. Lokalt kontor i Johanneshov. Boka ett kostnadsfritt möte.",
    image: kontaktStockholmOg,
    focusKeyword: "bokföring Stockholm",
    secondaryKeywords: [
      "redovisningsbyrå Stockholm",
      "digital bokföring Stockholm",
      "redovisningskonsult Stockholm",
      "löpande bokföring Stockholm",
    ],
  },
  bokslut: {
    key: "bokslut",
    path: "/bokslut",
    title: "Bokslut & deklaration för aktiebolag | Cloud",
    description:
      "Behöver du hjälp med bokslut, årsredovisning aktiebolag eller deklaration? Vi hjälper företag i Umeå, Stockholm och digitalt i hela Sverige.",
    image: bokslutOg,
    focusKeyword: "bokslut och deklaration",
    secondaryKeywords: [
      "årsredovisning aktiebolag",
      "deklaration aktiebolag",
      "bokslut Umeå",
      "redovisningsbyrå Stockholm",
    ],
  },
  arsredovisningAktiebolag: {
    key: "arsredovisning-aktiebolag",
    path: "/arsredovisning-aktiebolag",
    title: "Årsredovisning aktiebolag: K2/K3 | Cloud Redovisning",
    description: "Viktiga datum, arbetsgång och skillnaden mellan K2 och K3 för aktiebolag. Cloud hjälper företag i Umeå, Stockholm och digitalt.",
    image: bokslutOg,
    focusKeyword: "årsredovisning aktiebolag",
    secondaryKeywords: ["bokslut aktiebolag", "årsredovisning Umeå", "årsredovisning Stockholm"],
  },
  bytaRedovisningsbyra: {
    key: "byta-redovisningsbyra",
    path: "/byta-redovisningsbyra",
    title: "Byta redovisningsbyrå: så går det till | Cloud Redovisning",
    description: "Planera ett byte med tydliga steg för avtal, behörigheter, bokföringsmaterial och tidsplan. Cloud hjälper företag i Umeå, Stockholm och digitalt.",
    image: bokforingOg,
    focusKeyword: "byta redovisningsbyrå",
    secondaryKeywords: ["byte redovisningsbyrå", "ny redovisningskonsult", "redovisningsbyrå Umeå"],
  },
  priser: {
    key: "priser",
    path: "/priser",
    title: "Priser för redovisning och bokföring | Cloud Redovisning",
    description: "Se vad som påverkar priset på bokföring och redovisning. Cloud hjälper företag i Umeå, Stockholm och digitalt i hela Sverige.",
    image: radgivningOg,
    focusKeyword: "pris redovisningsbyrå",
    secondaryKeywords: ["pris bokföring företag", "kostnad redovisningsbyrå", "offert redovisning"],
  },
  radgivning: {
    key: "radgivning",
    path: "/radgivning",
    title: "Ekonomisk rådgivning för företag | Cloud",
    description:
      "Ekonomisk rådgivning, företagsrådgivning och lönsamhetsstyrning för företag i Umeå, Stockholm och hela Sverige. Boka rådgivning med Cloud.",
    image: radgivningOg,
    focusKeyword: "ekonomisk rådgivning företag",
    secondaryKeywords: [
      "företagsrådgivning ekonomi",
      "redovisningskonsult Stockholm",
      "3:12 regler rådgivning",
      "inhouse ekonomi",
    ],
  },
  inhouse: {
    key: "inhouse",
    path: "/inhouse",
    title: "Inhouse ekonomi & interim ekonomi | Cloud",
    description:
      "Behöver ni inhouse ekonomi eller interim ekonomi? Vi förstärker ekonomiavdelningar i Umeå, Stockholm och på distans i hela Sverige.",
    image: inhouseOg,
    focusKeyword: "inhouse ekonomi",
    secondaryKeywords: [
      "interim ekonomi",
      "inhouse redovisning",
      "ekonomikonsult Stockholm",
      "ekonomikonsult Umeå",
    ],
  },
  loneadministration: {
    key: "loneadministration",
    path: "/loneadministration",
    title: "Löneadministration i Umeå & Stockholm | Cloud",
    description:
      "Löneadministration för företag i Umeå, Stockholm och hela Sverige. Vi hjälper med lön, AGI, kollektivavtal, semester och lönebackup.",
    image: lonerOg,
    focusKeyword: "löneadministration Umeå",
    secondaryKeywords: [
      "löneadministration Stockholm",
      "lönehantering företag",
      "lönebackup",
      "redovisningsbyrå Umeå",
    ],
  },
  omOss: {
    key: "om-oss",
    path: "/om-oss",
    title: "Om Cloud Redovisning | FAR-auktoriserad byrå",
    description:
      "Lär känna Cloud Redovisning, en modern redovisningsbyrå med team i Umeå, Stockholm och stark närvaro i Vännäs. FAR-auktoriserad kompetens.",
    image: omOssOg,
    focusKeyword: "auktoriserad redovisningsbyrå",
    secondaryKeywords: [
      "FAR auktoriserad redovisningskonsult",
      "redovisningsbyrå Umeå",
      "redovisningsbyrå Stockholm",
      "om redovisningsbyrå",
    ],
  },
  medarbetare: {
    key: "medarbetare",
    path: "/medarbetare",
    title: "Redovisningskonsulter & lönespecialister | Cloud",
    description:
      "Möt våra redovisningskonsulter och lönespecialister i Umeå, Stockholm och Vännäs. Personlig service, auktoriserad kompetens och lokal närvaro.",
    image: teamOg,
    focusKeyword: "redovisningskonsult Umeå",
    secondaryKeywords: [
      "redovisningskonsult Stockholm",
      "FAR auktoriserad redovisningskonsult",
      "lönespecialist Umeå",
      "medarbetare redovisningsbyrå",
    ],
  },
  karriar: {
    key: "karriar",
    path: "/karriar",
    title: "Karriär på Cloud Redovisning | Umeå & Stockholm",
    description:
      "Utforska karriärmöjligheter på Cloud Redovisning i Umeå och Stockholm. För redovisningskonsulter, lönespecialister och rådgivare som vill utvecklas.",
    image: karriarOg,
    focusKeyword: "karriär redovisningsbyrå",
    secondaryKeywords: [
      "jobb redovisningskonsult Umeå",
      "jobb redovisningskonsult Stockholm",
      "jobb lönekonsult",
      "karriär ekonomi",
    ],
  },
  kundreferenser: {
    key: "kundreferenser",
    path: "/kundreferenser",
    title: "Kundreferenser för redovisning & rådgivning | Cloud",
    description:
      "Läs kundreferenser från företag i Umeå, Stockholm och Vännäs som fått hjälp med bokföring, löneadministration, rådgivning och digital omställning.",
    image: referenserOg,
    focusKeyword: "redovisningsbyrå kundreferenser",
    secondaryKeywords: [
      "bokföring kundcase",
      "digital bokföring kundreferens",
      "redovisningsbyrå Vännäs",
      "ekonomisk rådgivning företag",
    ],
  },
  nyheter: {
    key: "nyheter",
    path: "/nyheter",
    title: "Nyheter om redovisning, lön & tillväxt | Cloud",
    description:
      "Läs nyheter och insikter om bokföring, lön, deklaration, digitalisering och företagstillväxt från Cloud Redovisning i Umeå och Stockholm.",
    image: nyheterOg,
    focusKeyword: "digital bokföring",
    secondaryKeywords: [
      "bokslut och deklaration",
      "löneadministration",
      "redovisningsbyrå Umeå",
      "redovisningskonsult Stockholm",
    ],
  },
  kontakt: {
    key: "kontakt",
    path: "/kontakt",
    title: "Kontakta redovisningsbyrå i Umeå & Stockholm | Cloud",
    description:
      "Kontakta Cloud Redovisning i Umeå eller Stockholm. Vi hjälper företag med bokföring, lön, bokslut, deklaration och rådgivning i hela Sverige.",
    image: kontaktUmeaOg,
    focusKeyword: "redovisningsbyrå Umeå",
    secondaryKeywords: [
      "redovisningsbyrå Stockholm",
      "kontakta redovisningskonsult",
      "bokföring Umeå",
      "digital bokföring Sverige",
    ],
  },
  kontaktUmea: {
    key: "kontakt-umea",
    path: "/kontakt-umea",
    title: "Redovisningsbyrå i Umeå – auktoriserad & digital | Cloud",
    description:
      "Auktoriserad redovisningsbyrå på Pendelgatan 3b i Umeå. Hjälp med bokföring, lön, bokslut och rådgivning för företag i Umeå och Västerbotten.",
    image: kontaktUmeaOg,
    focusKeyword: "redovisningsbyrå Umeå",
    secondaryKeywords: [
      "bokföring Umeå",
      "löneadministration Umeå",
      "redovisningskonsult Umeå",
      "redovisningsbyrå Vännäs",
    ],
  },
  kontaktStockholm: {
    key: "kontakt-stockholm",
    path: "/kontakt-stockholm",
    title: "Redovisningsbyrå i Stockholm – Johanneshov | Cloud",
    description:
      "Redovisningsbyrå på Arenavägen 45 i Johanneshov. Bokföring, lön, bokslut och rådgivning för ägarledda företag i Stockholm.",
    image: kontaktStockholmOg,
    focusKeyword: "redovisningsbyrå Stockholm",
    secondaryKeywords: [
      "bokföring Stockholm",
      "löneadministration Stockholm",
      "redovisningskonsult Stockholm",
      "ekonomisk rådgivning företag",
    ],
  },
  integritetspolicy: {
    key: "integritetspolicy",
    path: "/integritetspolicy",
    title: "Integritetspolicy | Cloud Redovisning",
    description:
      "Läs hur Cloud Redovisning behandlar personuppgifter när du kontaktar oss om bokföring, lön, bokslut, deklaration eller rådgivning.",
    image: homeOg,
    focusKeyword: "integritetspolicy redovisningsbyrå",
    secondaryKeywords: [
      "personuppgifter redovisningsbyrå",
      "kontaktformulär integritet",
      "Cloud Redovisning policy",
    ],
  },
};

const faqByPage: Partial<Record<string, Array<{ question: string; answer: string }>>> = {
  bokforing: [
    {
      question: "Vad kostar hjälp med bokföring i Umeå eller Stockholm?",
      answer:
        "Priset beror på transaktionsvolym, bolagsform och hur mycket ni vill sköta själva. Cloud tar fram ett upplägg som passar företag i Umeå, Stockholm, Vännäs och digitalt i hela Sverige.",
    },
    {
      question: "Kan ni hjälpa till med digital bokföring i hela Sverige?",
      answer:
        "Ja. Vi arbetar digitalt i Fortnox och hjälper företag i Umeå, Stockholm, Vännäs och resten av Sverige med löpande bokföring, rapportering och rådgivning.",
    },
  ],
  "bokforing-umea": [
    {
      question: "Vad kostar bokföring i Umeå hos Cloud Redovisning?",
      answer:
        "Priset styrs av transaktionsvolym, bolagsform och vilka tjänster som ingår. Löpande bokföring för mindre bolag i Umeå ligger ofta mellan 800–4 000 kr/månad som riktmärke, men vi tar alltid fram ett skräddarsytt upplägg efter ett kostnadsfritt möte.",
    },
    {
      question: "Kan jag träffa min redovisningskonsult på plats i Umeå?",
      answer:
        "Ja. Vårt kontor ligger på Pendelgatan 3b i Umeå och du är alltid välkommen att boka ett fysiskt möte, även om bokföringsarbetet sköts digitalt i Fortnox.",
    },
    {
      question: "Hjälper ni företag utanför Umeå tätort, till exempel Vännäs?",
      answer:
        "Ja, vi har lång erfarenhet av att hjälpa företag i Vännäs och övriga Västerbotten, både på plats och helt digitalt.",
    },
  ],
  "bokforing-stockholm": [
    {
      question: "Vad kostar bokföring i Stockholm hos Cloud Redovisning?",
      answer:
        "Priset beror på transaktionsvolym, bolagsform och omfattning. Vi sätter alltid ihop ett skräddarsytt upplägg efter ett kostnadsfritt inledande möte, så att ni bara betalar för det ni faktiskt behöver.",
    },
    {
      question: "Var ligger ert kontor i Stockholm?",
      answer:
        "Vårt Stockholmskontor finns på Arenavägen 45, plan 16, i Johanneshov, nära Globen. Du är alltid välkommen att boka ett fysiskt möte där.",
    },
    {
      question: "Arbetar ni med företag i hela Stockholmsregionen, eller bara innerstaden?",
      answer:
        "Vi hjälper företag i hela Stockholmsregionen — innerstad, söderort, norrort och kranskommuner — samt digitalt i hela Sverige.",
    },
  ],
  bokslut: [
    {
      question: "När behöver ett aktiebolag årsredovisning?",
      answer:
        "Aktiebolag ska upprätta och lämna in årsredovisning enligt gällande regler efter räkenskapsårets slut. Cloud hjälper företag med årsredovisning, bokslut och deklaration i tid.",
    },
    {
      question: "Kan ni hjälpa med deklaration för aktiebolag?",
      answer:
        "Ja. Vi hjälper med inkomstdeklaration, K10, 3:12-frågor och skatteplanering för aktiebolag i Umeå, Stockholm och digitalt i hela Sverige.",
    },
  ],
  loneadministration: [
    {
      question: "Vad ingår i löneadministration?",
      answer:
        "Löneadministration omfattar löneberedning, löneutbetalningar, AGI, semester, sjukfrånvaro, förmåner, reseräkningar och stöd kring kollektivavtal.",
    },
    {
      question: "Kan ni vara lönebackup vid akut frånvaro?",
      answer:
        "Ja. Cloud erbjuder lönebackup för företag som behöver säkra lönehanteringen vid sjukdom, semester eller oväntade förändringar.",
    },
  ],
  radgivning: [
    {
      question: "Vad gör en redovisningskonsult i rådgivningsrollen?",
      answer:
        "En redovisningskonsult hjälper företag att förstå siffrorna, förbättra lönsamheten, planera skatt, analysera kassaflöde och fatta bättre ekonomiska beslut.",
    },
    {
      question: "Hur fungerar K10 och 3:12-reglerna?",
      answer:
        "K10 och 3:12-reglerna påverkar utdelningsutrymmet i aktiebolag. Cloud hjälper dig att planera ägaruttag, lön och skatt på ett strukturerat sätt.",
    },
  ],
  inhouse: [
    {
      question: "Vad innebär inhouse ekonomi eller interim ekonomi?",
      answer:
        "Det innebär att en erfaren ekonom eller redovisningsspecialist från Cloud förstärker er ekonomiavdelning på plats, hybrid eller helt digitalt under en period.",
    },
    {
      question: "Hjälper ni företag i både Umeå och Stockholm?",
      answer:
        "Ja. Vi hjälper företag i Umeå, Stockholm, Vännäs och övriga Sverige med interim ekonomi, inhouse-stöd och resursförstärkning.",
    },
  ],
};

const offices = {
  umea: {
    path: "/kontakt-umea",
    city: "Umeå",
    // Name exactly as shown on the office card of /kontakt-umea.
    name: "Cloud Redovisning och Administration",
    street: "Pendelgatan 3b",
    postalCode: "904 22",
    country: "SE",
    // Map marker on the page; check against the building before relying on it.
    geo: { latitude: 63.832, longitude: 20.262 },
    areaServed: ["Umeå", "Vännäs"],
    sameAs: SOCIAL_PROFILES,
  },
  stockholm: {
    path: "/kontakt-stockholm",
    city: "Johanneshov",
    name: "Cloud Redovisning Stockholm AB",
    street: "Arenavägen 45, plan 16",
    postalCode: "121 77",
    country: "SE",
    geo: { latitude: 59.295, longitude: 18.082 },
    areaServed: ["Stockholm"],
    sameAs: [] as string[],
  },
} as const;

export function absoluteUrl(pathOrUrl?: string) {
  if (!pathOrUrl) return SITE_URL;
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

export function buildSeoHead(
  page: SeoPageConfig,
  schemas: Schema[] = [],
  options?: { noindex?: boolean },
) {
  const canonical = absoluteUrl(page.path);
  const image = absoluteUrl(page.image ?? publicPageSeo.home.image);

  return {
    meta: [
      { title: page.title },
      { name: "description", content: page.description },
      { name: "robots", content: options?.noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: page.title },
      { name: "twitter:description", content: page.description },
      { name: "twitter:image", content: image },
      { property: "og:locale", content: "sv_SE" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:title", content: page.title },
      { property: "og:description", content: page.description },
      { property: "og:url", content: canonical },
      { property: "og:image", content: image },
      { property: "og:image:alt", content: page.title },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: schemas.map((schema) => ({
      type: "application/ld+json",
      children: JSON.stringify(schema),
    })),
  };
}

export function buildSiteSchemas(): Schema[] {
  // Every page: the site itself. The Organization lives on the home page only and the offices on
  // their contact pages (see buildOrganizationSchema and buildOfficeSchema).
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      name: SITE_NAME,
      url: SITE_URL,
      inLanguage: "sv-SE",
    },
  ];
}

export function buildOrganizationSchema(): Schema {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: STABLE_IMAGES.logo,
    sameAs: SOCIAL_PROFILES,
    // The home page says "Sedan 2003". TODO: client to confirm.
    foundingDate: "2003",
  };
}

export const breadcrumbTrails: Record<string, Array<{ name: string; path: string }>> = {
  "/arsredovisning-aktiebolag": [
    { name: "Hem", path: "/" },
    { name: "Bokslut & deklaration", path: "/bokslut" },
    { name: "Årsredovisning aktiebolag", path: "/arsredovisning-aktiebolag" },
  ],
  "/byta-redovisningsbyra": [
    { name: "Hem", path: "/" },
    { name: "Byta redovisningsbyrå", path: "/byta-redovisningsbyra" },
  ],
  "/priser": [
    { name: "Hem", path: "/" },
    { name: "Priser", path: "/priser" },
  ],
  "/kontakt": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Kontakt",
      "path": "/kontakt"
    }
  ],
  "/bokforing-stockholm": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Bokföring",
      "path": "/bokforing"
    },
    {
      "name": "Bokföring Stockholm",
      "path": "/bokforing-stockholm"
    }
  ],
  "/kontakt-umea": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Kontakt",
      "path": "/kontakt"
    },
    {
      "name": "Umeå",
      "path": "/kontakt-umea"
    }
  ],
  "/inhouse": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Inhouse ekonomi",
      "path": "/inhouse"
    }
  ],
  "/kontakt-stockholm": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Kontakt",
      "path": "/kontakt"
    },
    {
      "name": "Stockholm",
      "path": "/kontakt-stockholm"
    }
  ],
  "/radgivning": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Rådgivning",
      "path": "/radgivning"
    }
  ],
  "/loneadministration": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Löneadministration",
      "path": "/loneadministration"
    }
  ],
  "/bokslut": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Bokslut och deklaration",
      "path": "/bokslut"
    }
  ],
  "/integritetspolicy": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Integritetspolicy",
      "path": "/integritetspolicy"
    }
  ],
  "/medarbetare": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Medarbetare",
      "path": "/medarbetare"
    }
  ],
  "/bokforing-umea": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Bokföring",
      "path": "/bokforing"
    },
    {
      "name": "Bokföring Umeå",
      "path": "/bokforing-umea"
    }
  ],
  "/om-oss": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Om oss",
      "path": "/om-oss"
    }
  ],
  "/bokforing": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Bokföring",
      "path": "/bokforing"
    }
  ],
  "/karriar": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Karriär",
      "path": "/karriar"
    }
  ],
  "/nyheter": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Nyheter",
      "path": "/nyheter"
    }
  ],
  "/kundreferenser": [
    {
      "name": "Hem",
      "path": "/"
    },
    {
      "name": "Kundreferenser",
      "path": "/kundreferenser"
    }
  ]
};

// The breadcrumb trail of a page, or null when it has none (home, admin, CMS pages, unknown paths).
// Used both for the BreadcrumbList markup and for the visible breadcrumb, so they cannot differ.
export function getBreadcrumbTrail(pathname: string): Array<{ name: string; path: string }> | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (path === "/") return null;
  if (breadcrumbTrails[path]) return breadcrumbTrails[path];

  const m = path.match(/^\/(nyheter|kundreferenser)\/([^/]+)$/);
  if (m) {
    const article = articleBySlug(m[2]);
    const category = m[1] === "nyheter" ? "nyheter" : "referenser";
    if (article && article.category === category) {
      return [
        { name: "Hem", path: "/" },
        { name: m[1] === "nyheter" ? "Nyheter" : "Kundreferenser", path: `/${m[1]}` },
        { name: article.title, path },
      ];
    }
  }
  return null;
}

export function buildBreadcrumbSchema(items: Array<{ name: string; path: string }>): Schema {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function buildPageSchema(page: SeoPageConfig, type: string): Schema {
  return {
    "@context": "https://schema.org",
    "@type": type,
    name: page.title,
    description: page.description,
    url: absoluteUrl(page.path),
    inLanguage: "sv-SE",
    isPartOf: { "@id": WEBSITE_ID },
  };
}

export function buildServiceSchema(
  page: SeoPageConfig,
  serviceType: string,
  _serviceOutput?: string[],
): Schema {
  const officeKey =
    page.key === "bokforing-umea" ? "umea" : page.key === "bokforing-stockholm" ? "stockholm" : null;
  const office = officeKey ? offices[officeKey] : null;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${absoluteUrl(page.path)}#service`,
    name: serviceType,
    serviceType,
    url: absoluteUrl(page.path),
    description: page.description,
    // The location pages are provided by their office, the rest by the organisation.
    provider: office
      ? { "@type": "AccountingService", "@id": `${absoluteUrl(office.path)}#office`, name: office.name }
      : { "@type": "Organization", "@id": ORGANIZATION_ID, name: SITE_NAME },
    areaServed: office
      ? office.areaServed.map((name) => ({ "@type": "City", name }))
      : [
          { "@type": "City", name: "Umeå" },
          { "@type": "City", name: "Stockholm" },
          { "@type": "City", name: "Vännäs" },
          { "@type": "Country", name: "Sverige" },
        ],
  };
}

export function buildFaqSchema(pageKey: string): Schema | null {
  const items = faqByPage[pageKey];
  if (!items?.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function buildOfficeSchema(officeKey: keyof typeof offices): Schema {
  const office = offices[officeKey];
  const url = absoluteUrl(office.path);

  return {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    "@id": `${url}#office`,
    name: office.name,
    url,
    image: STABLE_IMAGES[officeKey],
    telephone: OFFICE_PHONE_E164,
    email: SITE_EMAIL,
    address: {
      "@type": "PostalAddress",
      streetAddress: office.street,
      postalCode: office.postalCode,
      addressLocality: office.city,
      addressCountry: office.country,
    },
    geo: { "@type": "GeoCoordinates", ...office.geo },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "17:00",
      },
    ],
    areaServed: office.areaServed.map((name) => ({ "@type": "City", name })),
    parentOrganization: { "@id": ORGANIZATION_ID },
    ...(office.sameAs.length > 0 ? { sameAs: [...office.sameAs] } : {}),
  };
}

// Only people whose role and contact details are shown on /medarbetare, worded as shown there.
export function buildPersonSchemas() {
  const person = (slug: string, name: string, jobTitle: string, telephone: string, email: string): Schema => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE_URL}/medarbetare#${slug}`,
    name,
    jobTitle,
    telephone,
    email,
    worksFor: { "@type": "Organization", "@id": ORGANIZATION_ID, name: SITE_NAME },
  });

  return [
    person("lisa-vilminko", "Lisa Vilminko", "Kontorschef Umeå · Auktoriserad redovisningskonsult", "+46706636040", "lisa.vilminko@cloudredovisning.se"),
    person("linus-sjogren", "Linus Sjögren", "Kontorschef Stockholm · Redovisningskonsult", "+46700121211", "linus.sjogren@cloudredovisning.se"),
    person("elin-morin", "Elin Morin", "Lönechef", "+46767805406", "elin.morin@cloudredovisning.se"),
  ];
}

export function buildCollectionSchema(
  page: SeoPageConfig,
  itemNames: string[],
  type = "CollectionPage",
): Schema {
  return {
    "@context": "https://schema.org",
    "@type": type,
    name: page.title,
    description: page.description,
    url: absoluteUrl(page.path),
    hasPart: itemNames.map((name) => ({
      "@type": "CreativeWork",
      name,
    })),
  };
}

export function buildArticleSeo(article: Article, category: "nyheter" | "referenser") {
  const path =
    category === "nyheter"
      ? `/nyheter/${article.slug}`
      : `/kundreferenser/${article.slug}`;
  const title =
    category === "nyheter"
      ? `${article.title} | Nyheter | Cloud`
      : `${article.title} | Kundreferens | Cloud`;

  return {
    key: article.slug,
    path,
    title,
    description: article.metaDescription || article.excerpt,
    image: article.hero,
    focusKeyword:
      category === "nyheter" ? "redovisning nyheter" : "redovisningsbyrå kundreferenser",
    secondaryKeywords:
      category === "nyheter"
        ? ["digital bokföring", "löneadministration", "bokslut och deklaration"]
        : ["bokföring", "ekonomisk rådgivning företag", "digital bokföring"],
  } satisfies SeoPageConfig;
}

// The build gives imported images a content-hash suffix (/assets/name-AbCd1234.webp). Structured
// data points at the un-hashed copy in public/images/articles instead (same file name).
function stableArticleImageUrl(url: string) {
  const m = url.match(/^\/assets\/(.+)-[A-Za-z0-9_-]{8}\.(\w+)$/);
  return m ? absoluteUrl(`/images/articles/${m[1]}.${m[2]}`) : absoluteUrl(url);
}

export function buildArticleSchema(article: Article, category: "nyheter" | "referenser"): Schema {
  return {
    "@context": "https://schema.org",
    "@type": category === "nyheter" ? "BlogPosting" : "Article",
    headline: article.title,
    description: article.metaDescription || article.excerpt,
    datePublished: article.date,
    dateModified: article.date,
    image: stableArticleImageUrl(article.hero),
    author: {
      "@type": "Organization",
      name: SITE_NAME,
    },
    publisher: {
      "@type": "Organization",
      "@id": ORGANIZATION_ID,
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: STABLE_IMAGES.logo,
      },
    },
    mainEntityOfPage:
      category === "nyheter"
        ? absoluteUrl(`/nyheter/${article.slug}`)
        : absoluteUrl(`/kundreferenser/${article.slug}`),
    articleSection: category === "nyheter" ? "Nyheter" : "Kundreferenser",
    inLanguage: "sv-SE",
  };
}

export function buildArticleHead(slug: string, category: "nyheter" | "referenser") {
  const article = articleBySlug(slug);

  if (!article || article.category !== category) {
    const fallback = category === "nyheter" ? publicPageSeo.nyheter : publicPageSeo.kundreferenser;
    return buildSeoHead(fallback, [
      buildPageSchema(fallback, "WebPage"),
      buildBreadcrumbSchema([
        { name: "Hem", path: "/" },
        { name: category === "nyheter" ? "Nyheter" : "Kundreferenser", path: fallback.path },
      ]),
    ], { noindex: true });
  }

  const seo = buildArticleSeo(article, category);

  return buildSeoHead(seo, [
    buildArticleSchema(article, category),
    buildBreadcrumbSchema(getBreadcrumbTrail(seo.path) ?? []),
  ]);
}
